// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import apiClient from "../config/api";
import { handleApiCall, buildParams } from "../utils/apiHelper";

export const workflowStatusService = {
  /**
   * Get all workflow statuses with filters
   */
  getWorkflowStatuses: async (filters = {}) => {
    const params = buildParams({
      skip: filters.skip || 0,
      limit: filters.limit || 10,
      is_active: filters.is_active !== undefined ? filters.is_active : true,
      is_system_status:
        filters.is_system_status !== undefined
          ? filters.is_system_status
          : true,
    });

    return handleApiCall(
      () => apiClient.get("/admin/workflow-statuses/", { params }),
      "Error fetching workflow statuses",
    );
  },

  /**
   * Get workflow status by ID
   */
  getWorkflowStatus: async (statusId) => {
    return handleApiCall(
      () => apiClient.get(`/admin/workflow-statuses/${statusId}`),
      "Error fetching workflow status",
    );
  },

  /**
   * Create new workflow status
   */
  createWorkflowStatus: async (statusData) => {
    return handleApiCall(
      () => apiClient.post("/admin/workflow-statuses/", statusData),
      "Error creating workflow status",
    );
  },

  /**
   * Update workflow status
   */
  updateWorkflowStatus: async (statusId, statusData) => {
    return handleApiCall(
      () => apiClient.put(`/admin/workflow-statuses/${statusId}`, statusData),
      "Error updating workflow status",
    );
  },

  /**
   * Delete workflow status
   */
  deleteWorkflowStatus: async (statusId) => {
    return handleApiCall(
      () => apiClient.delete(`/admin/workflow-statuses/${statusId}`),
      "Error deleting workflow status",
    );
  },

  /**
   * Get workflow API calls for a specific status
   */
  getWorkflowApiCalls: async (statusId, filters = {}) => {
    const params = buildParams({
      skip: filters.skip || 0,
      limit: filters.limit || 100,
      status_id: statusId,
    });

    return handleApiCall(
      () => apiClient.get("/admin/workflow-api-calls/", { params }),
      "Error fetching workflow API calls",
    );
  },

  /**
   * Get workflow API calls count
   */
  getWorkflowApiCallsCount: async (statusId) => {
    return handleApiCall(async () => {
      const params = buildParams({
        skip: 0,
        limit: 1,
        status_id: statusId,
      });
      const response = await apiClient.get("/admin/workflow-api-calls/", {
        params,
      });
      return response.data.total || 0;
    }, "Error fetching workflow API calls count");
  },

  /**
   * Create new workflow API call
   */
  createWorkflowApiCall: async (apiCallData) => {
    return handleApiCall(
      () => apiClient.post("/admin/workflow-api-calls/", apiCallData),
      "Error creating workflow API call",
    );
  },

  /**
   * Update workflow API call
   */
  updateWorkflowApiCall: async (apiCallId, apiCallData) => {
    return handleApiCall(
      () =>
        apiClient.put(`/admin/workflow-api-calls/${apiCallId}`, apiCallData),
      "Error updating workflow API call",
    );
  },

  /**
   * Delete workflow API call
   */
  deleteWorkflowApiCall: async (apiCallId) => {
    return handleApiCall(
      () => apiClient.delete(`/admin/workflow-api-calls/${apiCallId}`),
      "Error deleting workflow API call",
    );
  },

  /**
   * Get workflow email notifications for a specific status
   */
  getWorkflowEmailNotifications: async (statusId, filters = {}) => {
    const params = buildParams({
      skip: filters.skip || 0,
      limit: filters.limit || 100,
      status_id: statusId,
    });

    return handleApiCall(
      () => apiClient.get("/admin/workflow-email-notifications/", { params }),
      "Error fetching workflow email notifications",
    );
  },

  /**
   * Get total count of workflow email notifications for a specific status
   */
  getWorkflowEmailNotificationsCount: async (statusId) => {
    return handleApiCall(async () => {
      const params = buildParams({
        skip: 0,
        limit: 1,
        status_id: statusId,
      });
      const response = await apiClient.get(
        "/admin/workflow-email-notifications/",
        { params },
      );
      return response.data.total || 0;
    }, "Error fetching workflow email notifications count");
  },

  /**
   * Create new workflow email notification
   */
  createWorkflowEmailNotification: async (emailNotificationData) => {
    return handleApiCall(
      () =>
        apiClient.post(
          "/admin/workflow-email-notifications/",
          emailNotificationData,
        ),
      "Error creating workflow email notification",
    );
  },

  /**
   * Update workflow email notification
   */
  updateWorkflowEmailNotification: async (
    notificationId,
    emailNotificationData,
  ) => {
    return handleApiCall(
      () =>
        apiClient.put(
          `/admin/workflow-email-notifications/${notificationId}`,
          emailNotificationData,
        ),
      "Error updating workflow email notification",
    );
  },

  /**
   * Delete workflow email notification
   */
  deleteWorkflowEmailNotification: async (notificationId) => {
    return handleApiCall(
      () =>
        apiClient.delete(
          `/admin/workflow-email-notifications/${notificationId}`,
        ),
      "Error deleting workflow email notification",
    );
  },

  /**
   * Get email templates
   */
  getEmailTemplates: async (filters = {}) => {
    const params = buildParams({
      skip: filters.skip || 0,
      limit: filters.limit || 100,
      is_active: filters.is_active !== undefined ? filters.is_active : true,
    });

    return handleApiCall(
      () => apiClient.get("/admin/email-templates/", { params }),
      "Error fetching email templates",
    );
  },

  /**
   * Get email template by ID
   */
  getEmailTemplate: async (templateId) => {
    return handleApiCall(
      () => apiClient.get(`/admin/email-templates/${templateId}`),
      "Error fetching email template",
    );
  },

  /**
   * Create new email template
   */
  createEmailTemplate: async (templateData) => {
    return handleApiCall(
      () => apiClient.post("/admin/email-templates/", templateData),
      "Error creating email template",
    );
  },

  /**
   * Update email template
   */
  updateEmailTemplate: async (templateId, templateData) => {
    return handleApiCall(
      () => apiClient.put(`/admin/email-templates/${templateId}`, templateData),
      "Error updating email template",
    );
  },

  /**
   * Delete email template
   */
  deleteEmailTemplate: async (templateId) => {
    return handleApiCall(
      () => apiClient.delete(`/admin/email-templates/${templateId}`),
      "Error deleting email template",
    );
  },

  /**
   * Get workflow email notifications by template code
   */
  getWorkflowEmailNotificationsByTemplateCode: async (templateCode) => {
    return handleApiCall(
      () =>
        apiClient.get(`/admin/workflow-email-notifications/`, {
          params: {
            template_code: templateCode,
            skip: 0,
            limit: 100,
          },
        }),
      "Error fetching workflow email notifications by template code",
    );
  },

  /**
   * Export workflow configuration
   */
  exportWorkflowConfig: async () => {
    return handleApiCall(
      () =>
        apiClient.get("/admin/workflow-config/export", {
          responseType: "blob",
        }),
      "Error exporting workflow config",
    );
  },
};
